/**
 * Bulk Import API service — handles all import-related API calls.
 */

import apis from '../../utils/apis';
import type {
  ImportUploadResponse,
  ImportStatusResponse,
  ImportConfirmResponse,
  ImportDocumentsResponse,
  ImportHistoryResponse,
  ImportUserRow,
  ImportClientRow,
} from '../../types/import';

const BASE = '/import';

/**
 * Upload a file for user import.
 */
export async function uploadUsersImport(file: File): Promise<ImportUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apis({
    method: 'POST',
    url: `${BASE}/users/upload`,
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

/**
 * Upload a file for client import.
 */
export async function uploadClientsImport(file: File): Promise<ImportUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apis({
    method: 'POST',
    url: `${BASE}/clients/upload`,
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

/**
 * Get import job status and preview data.
 */
export async function getImportStatus(jobUuid: string): Promise<ImportStatusResponse> {
  const response = await apis.get(`${BASE}/${jobUuid}`);
  return response.data;
}

/**
 * Save edited preview rows.
 */
export async function updateImportPreview(
  jobUuid: string,
  rows: ImportUserRow[] | ImportClientRow[],
  summary?: Record<string, unknown>
): Promise<{ success: boolean; total_items: number }> {
  const response = await apis.put(`${BASE}/${jobUuid}/preview`, { rows, summary });
  return response.data;
}

/**
 * Confirm and execute the import.
 */
export async function confirmImport(jobUuid: string): Promise<ImportConfirmResponse> {
  const response = await apis.post(`${BASE}/${jobUuid}/confirm`);
  return response.data;
}

/**
 * Upload documents for a client import (ZIP or multi-file).
 */
export async function uploadImportDocuments(
  jobUuid: string,
  files: File[],
  mapping?: Record<string, string>,
  isZip?: boolean
): Promise<ImportDocumentsResponse> {
  const formData = new FormData();

  if (isZip && files.length === 1) {
    formData.append('zip_file', files[0]);
  } else {
    files.forEach((file) => formData.append('files', file));
    if (mapping) {
      formData.append('mapping', JSON.stringify(mapping));
    }
  }

  const response = await apis({
    method: 'POST',
    url: `${BASE}/${jobUuid}/documents`,
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

/**
 * Skip document upload and mark job as completed.
 */
export async function skipDocumentUpload(jobUuid: string): Promise<{ success: boolean }> {
  const response = await apis.post(`${BASE}/${jobUuid}/skip-documents`);
  return response.data;
}

/**
 * Get past import jobs.
 */
export async function getImportHistory(): Promise<ImportHistoryResponse> {
  const response = await apis.get(`${BASE}/history`);
  return response.data;
}

/**
 * Download client import template.
 */
export function getClientTemplateUrl(): string {
  // This returns a URL the browser can navigate to for download
  // The auth token will be added by the interceptor
  return `${BASE}/clients/template`;
}

/**
 * Download client import template (with auth).
 */
export async function downloadClientTemplate(): Promise<void> {
  const response = await apis({
    method: 'GET',
    url: `${BASE}/clients/template`,
    responseType: 'blob',
  });

  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'PONS_Client_Import_Template.xlsx';
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}
